import * as Api from '@ViewModels';
import { useQuery } from '@tanstack/react-query';
import { useMemo } from 'react';
import { useUserSession } from '../../../models/hooks/appStateHooks';
import type { IDailyActivity } from '../../viewModels/gamification';
import { queryKeys } from '../data/queryKeys';
import type { DialPacingSnapshot } from './types';

export type UseDialPacingResult = {
	snapshot: DialPacingSnapshot;
	refetch: () => void;
};

const REFETCH_INTERVAL_MS = 60_000;

/**
 * Fetches the rep's daily pacing from `userDailyActivity` and projects it
 * into the focused-view's `DialPacingSnapshot` discriminated union.
 *
 * Refetches every 60s while mounted. The `call:ended` invalidation is wired
 * in `FocusedLeadView` (it has the call pipeline; this hook stays
 * pipeline-agnostic).
 *
 * Failures fall through to `{ status: 'error' }` — `PacingStrip` renders
 * skeleton chips for both error and skeleton states, so the surface
 * degrades silently. Pacing is informational; never block.
 */
export function useDialPacing(): UseDialPacingResult {
	const userSession = useUserSession();

	const query = useQuery<IDailyActivity, Error>({
		queryKey: queryKeys.focusedView.pacing(),
		queryFn: async () => {
			// `callAsync<T>` resolves directly to `T` (and throws an `IOperationResultNoValue`
			// on failure) — see `extViewmodels/sdk/WebServiceHelper.ts`. This is the same
			// shape the legacy `GamificationViewModel.getScores()` uses for this endpoint,
			// so we don't need to unwrap an `IOperationResult` here.
			return userSession.webServiceHelper.callAsync<IDailyActivity>(
				Api.ImpersonationBroker.composeApiUrl({ urlPath: 'userDailyActivity' }),
				'GET'
			);
		},
		refetchInterval: REFETCH_INTERVAL_MS,
	});

	const snapshot = useMemo<DialPacingSnapshot>(() => {
		if (query.error) {
			return { status: 'error', message: query.error.message ?? 'Failed to load pacing' };
		}
		if (!query.data) {
			return { status: 'skeleton' };
		}
		return projectToSnapshot(query.data);
	}, [query.data, query.error]);

	return {
		snapshot,
		refetch: () => {
			void query.refetch();
		},
	};
}

/**
 * Maps an `IDailyActivity` (the raw legacy gamification payload) onto the
 * focused-view's `DialPacingSnapshot`. Treats a 0 / missing dial goal as
 * `null` so `PacingStrip` renders "X dials" instead of "X / 0".
 *
 * `pacePerHour` is computed client-side from the local clock — the API
 * doesn't return it. We guard the divide for the first ~6 minutes of the
 * day so we don't dump 600+ dials/hr on a freshly-clocked-in rep.
 */
function projectToSnapshot(activity: IDailyActivity): DialPacingSnapshot {
	const score = activity.score;
	const goals = activity.goals;
	const goalDials = goals && goals.dials > 0 ? goals.dials : null;

	const now = new Date();
	const hoursElapsed = now.getHours() + now.getMinutes() / 60;
	const pacePerHour = hoursElapsed >= 0.1 ? score.dials / hoursElapsed : 0;

	return {
		status: 'loaded',
		dialsToday: score.dials,
		dialGoal: goalDials,
		connectsToday: score.connections,
		demosBookedToday: score.demos,
		// Round to one decimal place — the strip displays this as e.g. "8.4/hr".
		pacePerHour: Math.round(pacePerHour * 10) / 10,
	};
}
